import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import { useMatch, useSearchParams } from "react-router-dom";
import { usePolicyContext } from "@piiqtechnologies/ui-lib";
import { useBillingEnabled } from "@hooks";
import { DiscoverAction } from "@constants/Policies";
import { buildOrgPath, ORG_SEGMENT, PATHS } from "@constants/Paths";
import { useAuthContext } from "@stores/AuthContext";
import { getIsAdmin } from "@stores/utils";
const PLANS_BILLING_PARAM = "view";
const PLANS_BILLING_VALUE = "plans-billing";
const REPORT_TYPES_VALUE = "report-types";
const CHECKOUT_VALUE = "checkout";
const ACCOUNT_SETTINGS_VALUE = "account-settings";
const PlansBillingContext = createContext(undefined);
export const PlansBillingProvider = ({ children }) => {
    var _a;
    const [searchParams, setSearchParams] = useSearchParams();
    const [selectedPlan, setSelectedPlan] = useState(undefined);
    const [selectedBillingPeriod, setSelectedBillingPeriod] = useState(undefined);
    const billingEnabled = useBillingEnabled();
    const canManageUsers = usePolicyContext(DiscoverAction.ManageUsers);
    // This provider is mounted once in AuthLayout, which sits ABOVE ActiveOrgLayout — the only thing
    // that mounts ActiveOrganizationProvider. So the org context genuinely is not readable from here:
    // useOptionalActiveOrganization() would return undefined for every user, member or staff, and the
    // member branch below would be dead code. Consumers like AccountSettings can read the context
    // because they render below ActiveOrgLayout; this provider cannot.
    //
    // The org segment is read off the URL instead, which is available at any depth. That is safe
    // because this only picks a navigation destination and grants nothing: ActiveOrgLayout
    // re-authorizes the `:orgId` against real memberships on arrival, so a hand-edited segment lands
    // on Restricted exactly as typing it into the address bar would.
    const { user } = useAuthContext();
    const isAdmin = getIsAdmin(user);
    const activeOrgId = (_a = useMatch({ path: ORG_SEGMENT, end: false })) === null || _a === void 0 ? void 0 : _a.params.orgId;
    const closeDestination = isAdmin
        ? PATHS.ADMIN_DASHBOARD
        : activeOrgId
            ? buildOrgPath(activeOrgId, PATHS.HOME)
            : PATHS.RESTRICTED;
    const viewParam = searchParams.get(PLANS_BILLING_PARAM);
    const isPlansBillingOpen = billingEnabled &&
        (viewParam === PLANS_BILLING_VALUE || viewParam === REPORT_TYPES_VALUE || viewParam === CHECKOUT_VALUE);
    const isAccountSettingsOpen = canManageUsers && viewParam === ACCOUNT_SETTINGS_VALUE;
    const plansBillingView = viewParam === REPORT_TYPES_VALUE ? "report-types" : viewParam === CHECKOUT_VALUE ? "checkout" : "billing";
    const openPlansBilling = useCallback(() => {
        if (!billingEnabled)
            return;
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.set(PLANS_BILLING_PARAM, PLANS_BILLING_VALUE);
            return next;
        });
    }, [billingEnabled, setSearchParams]);
    const closePlansBilling = useCallback(() => {
        window.location.assign(closeDestination);
    }, [closeDestination]);
    const openAccountSettings = useCallback(() => {
        if (!canManageUsers)
            return;
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.set(PLANS_BILLING_PARAM, ACCOUNT_SETTINGS_VALUE);
            return next;
        });
    }, [canManageUsers, setSearchParams]);
    const closeAccountSettings = useCallback(() => {
        window.location.assign(closeDestination);
    }, [closeDestination]);
    const setPlansBillingView = useCallback((view) => {
        if (!billingEnabled)
            return;
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            const valueMap = {
                "report-types": REPORT_TYPES_VALUE,
                checkout: CHECKOUT_VALUE,
                billing: PLANS_BILLING_VALUE,
            };
            next.set(PLANS_BILLING_PARAM, valueMap[view]);
            return next;
        });
    }, [billingEnabled, setSearchParams]);
    const contextValue = useMemo(() => ({
        isPlansBillingOpen,
        plansBillingView,
        selectedPlan,
        selectedBillingPeriod,
        openPlansBilling,
        closePlansBilling,
        setPlansBillingView,
        setSelectedPlan,
        setSelectedBillingPeriod,
        isAccountSettingsOpen,
        openAccountSettings,
        closeAccountSettings,
    }), [
        isPlansBillingOpen,
        plansBillingView,
        selectedPlan,
        selectedBillingPeriod,
        openPlansBilling,
        closePlansBilling,
        setPlansBillingView,
        setSelectedPlan,
        setSelectedBillingPeriod,
        isAccountSettingsOpen,
        openAccountSettings,
        closeAccountSettings,
    ]);
    return React.createElement(PlansBillingContext.Provider, { value: contextValue }, children);
};
export const usePlansBillingContext = () => {
    const context = useContext(PlansBillingContext);
    if (context === undefined) {
        throw new Error("usePlansBillingContext must be used within a PlansBillingProvider");
    }
    return context;
};
export const useOptionalPlansBillingContext = () => useContext(PlansBillingContext);
